import { parseProject, type Project } from "./schema";
import { bytesHash } from "./fonts";

type Data = Record<string, any>;
export type RevisionMatches = Record<string, string | null>;
export interface RevisionMatch {
  key: string;
  collection: string[];
  incomingId: string;
  incomingName: string;
  currentId?: string;
  reason: "identity" | "source" | "name" | "manual" | "new" | "ambiguous";
  candidates: { id: string; name: string }[];
}
export interface RevisionChange {
  id: string;
  collection: string[];
  entity?: string;
  path: string[];
  label: string;
  kind: "add" | "remove" | "set" | "order";
  before?: unknown;
  after?: unknown;
}
export interface RevisionPlan {
  version: 1;
  currentHash: string;
  incomingHash: string;
  matches: RevisionMatch[];
  changes: RevisionChange[];
  incoming: Project;
}
export type RevisionDecisions = Record<string, "current" | "incoming">;
const roots = [
  "fields",
  "groups",
  "tabs",
  "triggers",
  "parts",
  "styles",
  "outputs",
  "datasets",
  "forms",
  "formInstances",
  "dynamicSources",
  "fontManifest",
  "pages",
];
const children = ["nodes", "layoutGroups", "flowRegions"];
const clone = <T>(value: T): T => structuredClone(value);
const canonical = (value: unknown): string =>
  JSON.stringify(value, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.keys(item)
            .sort()
            .map((key) => [key, item[key]]),
        )
      : item,
  );
const hash = (value: unknown) =>
  bytesHash(new TextEncoder().encode(canonical(value)));
const equal = (a: unknown, b: unknown) => canonical(a) === canonical(b);
const token = (value: Data) =>
  value.version && value.columns
    ? `${value.id}@${value.version}`
    : String(value.id);
const name = (value: Data) =>
  String(value.label || value.name || value.key || value.id);
const matchName = (value: Data, kind: string) =>
  kind === "nodes" || kind === "layoutGroups" || kind === "flowRegions"
    ? `${name(value)} · ${value.type || value.direction || "region"} at ${Number(value.x || 0).toFixed(1)}, ${Number(value.y || 0).toFixed(1)}${value.width && value.height ? ` (${Number(value.width).toFixed(1)} × ${Number(value.height).toFixed(1)})` : ""}`
    : name(value);
const matchKey = (collection: string[], id: string) =>
  JSON.stringify([...collection, id]);
const object = (value: unknown): value is Data =>
  !!value && typeof value === "object" && !Array.isArray(value);
function array(project: Data, path: string[], create = false): Data[] {
  let target: any = project;
  for (const [index, key] of path.entries()) {
    if (Array.isArray(target))
      target = target.find((item) => token(item) === key);
    else if (target) {
      if (create && index === path.length - 1 && !Object.hasOwn(target, key))
        target[key] = [];
      target = target[key];
    }
  }
  return Array.isArray(target) ? target : [];
}
function source(project: Project, item: Data, kind: string) {
  const imported = [
    project.legacy?.documentImport?.items,
    project.legacy?.targetImport?.items,
  ].flatMap((items) => (Array.isArray(items) ? items : []));
  const entry =
    Array.isArray(imported) &&
    imported.find(
      (entry: Data) =>
        entry &&
        typeof entry === "object" &&
        (kind === "nodes" ? entry.nodeId : entry.fieldId) === item.id,
    );
  return kind === "nodes" || kind === "fields"
    ? String(
        (kind === "fields" &&
          (item.legacy?.textVariableSourceId || item.legacy?.sourceId)) ||
          entry?.sourceId ||
          "",
      )
    : "";
}
function semantic(item: Data, kind: string) {
  if (kind === "fields") return String(item.key || "");
  if (kind === "parts") return String(item.sku || "");
  if (kind === "styles" && item.role) return `role:${item.role}`;
  return `${name(item)}${kind === "nodes" ? `:${item.type}` : kind === "outputs" ? `:${item.format}:${!!item.includeProduction}` : kind === "datasets" ? `:${item.version}` : ""}`;
}

/** Explicit two-way review. No imported deletions or edits are accepted implicitly. */
export function planProjectRevision(
  currentInput: Project,
  incomingInput: Project,
  selections: RevisionMatches = {},
): RevisionPlan {
  const current = parseProject(currentInput),
    incoming = parseProject(incomingInput);
  if (current.tenantId !== incoming.tenantId)
    throw new Error("Revision tenant does not match the current project");
  for (const face of incoming.fontManifest) {
    const existing = current.fontManifest.find((value) => value.id === face.id);
    if (existing && !equal(existing, face))
      throw new Error(`Conflicting immutable font metadata: ${face.name}`);
  }
  const matches: RevisionMatch[] = [],
    ids = new Map<string, string>(),
    regions = new Map<string, Map<string, string>>();
  const occupied = new Set<string>();
  for (const root of roots)
    for (const item of (current as Data)[root] || []) {
      if (root === "fontManifest") continue;
      occupied.add(item.id);
      if (root === "pages")
        for (const kind of children)
          for (const child of item[kind] || []) occupied.add(child.id);
    }
  const pair = (
    incomingItems: Data[],
    currentItems: Data[],
    collection: string[],
    into = ids,
  ) => {
    const kind = collection.at(-1)!,
      used = new Set<string>();
    const catalogue = currentItems.map((value) => ({
      id: token(value),
      name: matchName(value, kind),
    }));
    const currentSources = new Map(
      currentItems.map((value) => [token(value), source(current, value, kind)]),
    );
    const reserved = new Set(
      incomingItems
        .map((item) => selections[matchKey(collection, token(item))])
        .filter((value): value is string => typeof value === "string"),
    );
    for (const item of incomingItems) {
      const key = matchKey(collection, token(item));
      const selected = selections[key];
      const candidates = currentItems.filter(
        (candidate) =>
          !used.has(token(candidate)) &&
          (!reserved.has(token(candidate)) || selected === token(candidate)) &&
          (kind !== "datasets" || candidate.version === item.version),
      );
      let chosen: Data | undefined,
        reason: RevisionMatch["reason"] = "new";
      if (selected !== undefined) {
        if (selected !== null) {
          chosen = candidates.find(
            (candidate) => token(candidate) === selected,
          );
          if (!chosen)
            throw new Error(
              `Invalid or repeated revision match for ${name(item)}`,
            );
        }
        reason = "manual";
      } else {
        chosen = candidates.find(
          (candidate) => token(candidate) === token(item),
        );
        if (chosen) reason = "identity";
        else if (kind !== "fontManifest") {
          const from = current.legacy?.documentImport,
            to = incoming.legacy?.documentImport;
          const comparable =
            (!from?.format || !to?.format || from.format === to.format) &&
            (!["pdf", "image"].includes(to?.format) ||
              (!!to?.sha256 && from?.sha256 === to.sha256));
          const sourceId = comparable ? source(incoming, item, kind) : "";
          const sameSource = sourceId
            ? candidates.filter(
                (candidate) =>
                  currentSources.get(token(candidate)) === sourceId,
              )
            : [];
          const sameName = candidates.filter(
            (candidate) => semantic(candidate, kind) === semantic(item, kind),
          );
          if (sameSource.length === 1) {
            chosen = sameSource[0];
            reason = "source";
          } else if (sameSource.length > 1) reason = "ambiguous";
          else if (sameName.length === 1 && semantic(item, kind)) {
            chosen = sameName[0];
            reason = "name";
          } else if (sameName.length > 1) reason = "ambiguous";
        }
      }
      if (chosen) used.add(token(chosen));
      let assigned = chosen?.id || into.get(item.id) || item.id;
      if (
        kind !== "fontManifest" &&
        !chosen &&
        occupied.has(assigned) &&
        !into.has(item.id)
      )
        assigned = `revision_${hash([current.id, collection, item.id]).slice(0, 32)}`;
      if (into.has(item.id) && into.get(item.id) !== assigned)
        throw new Error(`Conflicting revision identity: ${name(item)}`);
      into.set(item.id, assigned);
      occupied.add(assigned);
      matches.push({
        key,
        collection,
        incomingId: token(item),
        incomingName: matchName(item, kind),
        currentId: chosen && token(chosen),
        reason,
        candidates: catalogue,
      });
    }
  };
  for (const root of roots)
    pair(
      (incoming as Data)[root] || [],
      (current as Data)[root] || [],
      [root],
      root === "fontManifest" ? new Map<string, string>() : ids,
    );
  for (const page of incoming.pages) {
    const existing = current.pages.find(
      (candidate) => candidate.id === ids.get(page.id),
    );
    for (const kind of children) {
      const regionMap =
        kind === "flowRegions" ? new Map<string, string>() : ids;
      pair(
        (page as Data)[kind] || [],
        (existing as Data)?.[kind] || [],
        ["pages", page.id, kind],
        regionMap,
      );
      if (kind === "flowRegions") regions.set(page.id, regionMap);
    }
  }
  if (
    Object.keys(selections).some(
      (key) => !matches.some((match) => match.key === key),
    )
  )
    throw new Error("Unknown revision match selection");
  const remap = (value: string | undefined) =>
    value === undefined ? value : ids.get(value) || value;
  const record = (values: Data = {}) =>
    Object.fromEntries(
      Object.entries(values).map(([key, value]) => [remap(key)!, clone(value)]),
    );
  const expression = (value: any, mapping = ids): any => {
    if (Array.isArray(value))
      return value.map((item) => expression(item, mapping));
    if (!object(value) || value.op === "literal") return clone(value);
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        (key === "fieldId" || key === "field") && typeof item === "string"
          ? mapping.get(item) || item
          : expression(item, mapping),
      ]),
    );
  };
  const field = (value: Data, mapping = ids): Data => {
    const result = clone(value),
      local = (id: string) => mapping.get(id) || id;
    result.id = local(value.id);
    if (result.groupId) result.groupId = remap(result.groupId);
    if (result.source)
      for (const key of [
        "datasetId",
        "dynamicSourceId",
        "lookupFieldId",
        "instanceId",
      ])
        if (result.source[key])
          result.source[key] =
            key === "lookupFieldId"
              ? local(result.source[key])
              : remap(result.source[key]);
    if (result.calculation)
      result.calculation = expression(result.calculation, mapping);
    if (result.visibleWhen)
      result.visibleWhen = expression(result.visibleWhen, mapping);
    if (result.presets)
      result.presets = result.presets.map((preset: Data) => ({
        ...preset,
        ...(preset.match ? { match: expression(preset.match, mapping) } : {}),
        values: Object.fromEntries(
          Object.entries(preset.values).map(([key, item]) => [
            local(key),
            item,
          ]),
        ),
      }));
    return result;
  };
  const normalized: Data = clone(incoming),
    formFields = new Map<string, Map<string, string>>();
  for (const root of roots)
    normalized[root] = ((incoming as Data)[root] || []).map((value: Data) => ({
      ...clone(value),
      id: root === "fontManifest" ? value.id : remap(value.id),
    }));
  normalized.fields = incoming.fields.map((value) => field(value));
  normalized.forms = incoming.forms.map((form) => {
    const existing = current.forms.find(
      (candidate) => candidate.id === remap(form.id),
    );
    const mapping = new Map<string, string>();
    for (const value of form.fields) {
      const candidates =
        existing?.fields.filter(
          (candidate) =>
            candidate.id === value.id || candidate.key === value.key,
        ) || [];
      mapping.set(
        value.id,
        candidates.length === 1 ? candidates[0].id : value.id,
      );
    }
    formFields.set(form.id, mapping);
    return {
      ...clone(form),
      id: remap(form.id),
      fields: form.fields.map((value) => field(value, mapping)),
    };
  });
  normalized.formInstances = incoming.formInstances.map((instance) => ({
    ...instance,
    id: remap(instance.id),
    formId: remap(instance.formId),
    ...(instance.inputOnly
      ? {
          inputOnly: instance.inputOnly.map(
            (id) => formFields.get(instance.formId)?.get(id) || id,
          ),
        }
      : {}),
    mapping: Object.fromEntries(
      Object.entries(instance.mapping).map(([key, value]) => [
        formFields.get(instance.formId)?.get(key) || key,
        remap(value),
      ]),
    ),
  }));
  normalized.parts = incoming.parts.map((part) => ({
    ...clone(part),
    id: remap(part.id),
    systemValues: record(part.systemValues),
    overrides: Object.fromEntries(
      Object.entries(part.overrides).map(([key, patch]) => {
        const original = incoming.fields.find((value) => value.id === key)!;
        const rewritten = field({ ...patch, id: key });
        delete rewritten.id;
        // Only authored patches travel. Never resolve inherited defaults into overrides.
        return [remap(original.id), rewritten];
      }),
    ),
    ...(part.dynamicSourcePolicies
      ? { dynamicSourcePolicies: record(part.dynamicSourcePolicies) }
      : {}),
  }));
  normalized.tabs = incoming.tabs.map((tab) => ({
    ...tab,
    id: remap(tab.id),
    groupIds: tab.groupIds.map((value) => remap(value)),
  }));
  normalized.triggers = incoming.triggers.map((trigger) => ({
    ...trigger,
    id: remap(trigger.id),
    sourceId: remap(trigger.sourceId),
    show: trigger.show.map((value) => remap(value)),
    hide: trigger.hide.map((value) => remap(value)),
    clear: trigger.clear.map((value) => remap(value)),
    exclude: record(trigger.exclude),
  }));
  normalized.pages = incoming.pages.map((page) => {
    const region = (value: string | undefined) =>
      value === undefined ? value : regions.get(page.id)?.get(value) || value;
    return {
      ...clone(page),
      id: remap(page.id),
      flowRegions: page.flowRegions.map((value) => ({
        ...value,
        id: region(value.id),
        ...(value.nextRegionId
          ? { nextRegionId: region(value.nextRegionId) }
          : {}),
      })),
      ...(page.layoutGroups === undefined
        ? {}
        : {
            layoutGroups: page.layoutGroups.map((group) => ({
              ...group,
              id: remap(group.id),
              ...(group.parentId ? { parentId: remap(group.parentId) } : {}),
              ...(group.visibleWhen
                ? { visibleWhen: expression(group.visibleWhen) }
                : {}),
            })),
          }),
      nodes: page.nodes.map((node) => {
        const result: Data = { ...clone(node), id: remap(node.id) };
        for (const key of ["fieldId", "styleId", "layoutGroupId"])
          if (result[key]) result[key] = remap(result[key]);
        if (result.flowRegionId)
          result.flowRegionId = region(result.flowRegionId);
        for (const key of ["visibleWhen", "textCalculation"])
          if (result[key]) result[key] = expression(result[key]);
        if (result.textTemplate)
          result.textTemplate = result.textTemplate.map((part: Data) =>
            part.fieldId ? { ...part, fieldId: remap(part.fieldId) } : part,
          );
        return result;
      }),
    };
  });
  for (const key of ["headerStyleId", "footerStyleId"])
    if (normalized.settings[key])
      normalized.settings[key] = remap(normalized.settings[key]);
  if (normalized.documentSettings.defaultStyleId)
    normalized.documentSettings.defaultStyleId = remap(
      normalized.documentSettings.defaultStyleId,
    );
  // Import provenance records refer to native IDs but their source IDs and opaque payloads do not.
  const report = normalized.legacy?.documentImport;
  if (report && Array.isArray(report.items)) {
    for (const item of report.items) {
      for (const key of ["pageId", "nodeId", "fieldId"])
        if (item[key]) item[key] = remap(item[key]);
      if (item.fieldIds)
        item.fieldIds = item.fieldIds.map((id: string) => remap(id));
    }
    for (const item of report.production || [])
      item.pageId = remap(item.pageId);
  }
  const changes: RevisionChange[] = [];
  const add = (change: Omit<RevisionChange, "id">) =>
    changes.push({
      ...change,
      id: hash([
        change.collection,
        change.entity,
        change.path,
        change.kind,
      ]).slice(0, 32),
    });
  const properties = (
    before: Data,
    after: Data,
    collection: string[],
    entity: string | undefined,
    label: string,
    path: string[] = [],
  ) => {
    for (const key of new Set([
      ...Object.keys(before),
      ...Object.keys(after),
    ])) {
      if (!path.length && key === "id") continue;
      if (
        collection[0] === "pages" &&
        collection.length === 1 &&
        !path.length &&
        children.includes(key)
      )
        continue;
      const previous = before[key],
        next = after[key];
      if (equal(previous, next)) continue;
      const location = [...path, key];
      if (
        key !== "legacy" &&
        object(previous) &&
        object(next) &&
        Object.keys(previous).length &&
        Object.keys(next).length
      )
        properties(previous, next, collection, entity, label, location);
      else
        add({
          collection,
          entity,
          path: location,
          label: `${label} · ${location.join(" / ")}`,
          kind: "set",
          before: clone(previous),
          after: clone(next),
        });
    }
  };
  const compare = (before: Data[], after: Data[], collection: string[]) => {
    for (const item of before) {
      const replacement = after.find((value) => token(value) === token(item));
      if (replacement)
        properties(item, replacement, collection, token(item), name(item));
      else
        add({
          collection,
          entity: token(item),
          path: [],
          label: name(item),
          kind: "remove",
          before: clone(item),
        });
    }
    for (const item of after)
      if (!before.some((value) => token(value) === token(item)))
        add({
          collection,
          entity: token(item),
          path: [],
          label: name(item),
          kind: "add",
          after: clone(item),
        });
    const oldOrder = before.map(token),
      newOrder = after.map(token);
    if (!equal(oldOrder, newOrder))
      add({
        collection,
        path: [],
        label: `${collection.at(-1)} order`,
        kind: "order",
        before: oldOrder,
        after: newOrder,
      });
  };
  for (const root of roots)
    compare((current as Data)[root] || [], normalized[root] || [], [root]);
  for (const page of normalized.pages)
    if (current.pages.some((existing) => existing.id === page.id))
      for (const kind of children)
        compare(array(current, ["pages", page.id, kind]), page[kind] || [], [
          "pages",
          page.id,
          kind,
        ]);
  properties(
    Object.fromEntries(
      [
        "name",
        "description",
        "instructions",
        "settings",
        "documentSettings",
        "resourceSnapshots",
        "assetLibrary",
        "legacy",
      ].map((key) => [key, (current as Data)[key]]),
    ),
    Object.fromEntries(
      [
        "name",
        "description",
        "instructions",
        "settings",
        "documentSettings",
        "resourceSnapshots",
        "assetLibrary",
        "legacy",
      ].map((key) => [key, normalized[key]]),
    ),
    [],
    undefined,
    "Project",
  );
  if (changes.length > 30000)
    throw new Error(
      "Revision has more than 30,000 changes; split the import into smaller documents",
    );
  return {
    version: 1,
    currentHash: hash(current),
    incomingHash: hash(incoming),
    matches,
    changes,
    incoming: normalized as Project,
  };
}

/** Apply reviewed changes atomically in memory; invalid references block the whole merge. */
export function applyProjectRevision(
  currentInput: Project,
  plan: RevisionPlan,
  decisions: RevisionDecisions,
): Project {
  const current = parseProject(currentInput);
  if (plan.version !== 1 || hash(current) !== plan.currentHash)
    throw new Error(
      "The current draft changed. Restart revision review before applying",
    );
  if (
    Object.keys(decisions).some(
      (id) => !plan.changes.some((change) => change.id === id),
    )
  )
    throw new Error("Unknown revision decision");
  if (
    Object.values(decisions).some(
      (value) => value !== "current" && value !== "incoming",
    )
  )
    throw new Error("Invalid revision decision");
  const result: Data = clone(current);
  const selected = plan.changes.filter(
    (change) => decisions[change.id] === "incoming",
  );
  for (const change of selected.filter((change) => change.kind !== "order")) {
    let target: any = result;
    if (change.collection.length) {
      const container = array(result, change.collection, change.kind === "add");
      if (change.kind === "add") {
        if (!object(change.after))
          throw new Error("Invalid added revision entity");
        container.push(clone(change.after));
        continue;
      }
      const index = container.findIndex(
        (value) => token(value) === change.entity,
      );
      if (index < 0)
        throw new Error(`Revision dependency is missing for ${change.label}`);
      if (change.kind === "remove") {
        container.splice(index, 1);
        continue;
      }
      target = container[index];
    }
    for (const key of change.path.slice(0, -1)) {
      if (!Object.hasOwn(target, key) || !object(target[key]))
        Object.defineProperty(target, key, {
          value: {},
          writable: true,
          configurable: true,
          enumerable: true,
        });
      target = target[key];
    }
    const key = change.path.at(-1)!;
    if (change.after === undefined) delete target[key];
    else
      Object.defineProperty(target, key, {
        value: clone(change.after),
        writable: true,
        configurable: true,
        enumerable: true,
      });
  }
  for (const change of selected.filter((change) => change.kind === "order")) {
    const container = array(result, change.collection),
      desired = change.after as string[];
    const positions = new Map(desired.map((id, index) => [id, index]));
    container.sort(
      (a, b) =>
        (positions.get(token(a)) ?? desired.length) -
        (positions.get(token(b)) ?? desired.length),
    );
  }
  result.id = current.id;
  result.tenantId = current.tenantId;
  result.revision = current.revision;
  result.status = "draft";
  try {
    return parseProject(result);
  } catch (error) {
    throw new Error(
      `The selected changes cannot be combined: ${(error as Error).message}. Review dependent fields, layers and configuration together`,
    );
  }
}
